/**
 * Il pronto-merce: quando la roba è pronta da ritirare, e se l'autista ci passerebbe prima.
 *
 * ## Le due domande, e perché stanno insieme
 *
 * Chieste da Cosimo il 2026-10-12: *«adesso dichiariamo anche il giorno di approntamento merce
 * oltre all'ora giusto? se non viene dichiarato dai per scontato che sia oggi e scrivilo sulla
 * pill. altrimenti metti la data e la ora in cui sarà pronta la merce e fai in modo di avvisare
 * chi crea il viaggio se lo assegna a un autista che ci passerebbe giorni prima del pronto
 * merce.»*
 *
 * Stanno insieme perché sono la stessa aritmetica vista da due lati: **quando è pronta** e
 * **quando ci passa il furgone**. La seconda senza la prima non si può fare, e la prima senza la
 * seconda è una data che nessuno usa.
 *
 * ## Il giorno c'era già, ed è la scheda che lo nascondeva
 *
 * `transfers.goods_ready_at` è un `timestamptz`: **porta il giorno e l'ora** dal primo giorno.
 * La scheda del Costruttore mostrava solo `pronta 12:00`. Misurato il 2026-10-12 sulle 498
 * spedizioni senza viaggio:
 *
 * | | |
 * |---|---|
 * | senza pronto-merce dichiarato | **309** (il 62%) |
 * | con pronto-merce | 189 — di cui **187 nel passato**, 2 oggi, **zero nel futuro** |
 * | le ore scritte | 14:00, 09:00, 08:00, 12:00, 10:00… — orari di lavoro veri, non mezzanotte |
 *
 * Cioè: mostrare la sola ora non è «meno informazione», è **informazione sbagliata** — `pronta
 * 12:00` su una merce pronta tre settimane fa si legge «oggi alle 12».
 *
 * ## ⚠ Cecità dichiarata (§84.6)
 *
 * `ciPassaPrima` **non è stata convalidata su dati veri**, e non per distrazione: oggi in
 * archivio non c'è **nessuna** spedizione con il pronto-merce nel futuro, quindi il caso che
 * l'avviso esiste per prendere non si dà. È provata sui casi costruiti qui sotto, e la prima
 * volta che una spedizione avrà una data futura varrà la pena guardare che l'avviso compaia
 * davvero — perché un verde su zero casi veri non è un verde (K105).
 *
 * ## «Non dichiarato» non è «pronta adesso»
 *
 * Quando `goodsReadyAt` è nullo si assume **oggi**, come ha deciso Cosimo — ma l'assunzione si
 * **dichiara**: `presunto: true`, e la scheda scrive che è una presunzione. La differenza conta
 * il giorno in cui qualcuno guarda un avviso che non è scattato: «non l'ho avvisata perché per
 * me era pronta oggi» è una risposta; «era pronta oggi» sarebbe una bugia.
 */
import { calendarDaysBetween, chiavePiuGiorni, dayKey, giornoDaChiave } from './format.js';
/**
 * Il pronto-merce di una spedizione, dichiarato o presunto.
 *
 * `adesso` si passa invece di leggerlo qui: una funzione che chiede l'ora al sistema non si può
 * provare su un giorno diverso da quello in cui gira.
 */
export function prontoMerce(dichiarato, adesso) {
    if (dichiarato instanceof Date && !Number.isNaN(dichiarato.getTime())) {
        return { istante: dichiarato, presunto: false };
    }
    /*
      L'inizio di oggi e non «adesso»: «pronta oggi» vuol dire che in giornata si può ritirare, e
      un furgone che passa alle 9 con l'assunzione fatta alle 11 si vedrebbe un avviso per una
      merce che nessuno ha detto non essere pronta. Fra le due letture si prende quella che **non**
      inventa un ostacolo.
    */
    /*
      L'inizio di oggi **a Roma**, non sull'orologio di chi chiama: con `setHours(0)` un PC col
      fuso sbagliato (o il server in UTC) spostava la mezzanotte presunta di una o due ore, e
      fra mezzanotte e l'una «oggi» era ancora ieri.
    */
    return { istante: giornoDaChiave(dayKey(adesso)), presunto: true };
}
/** Quanto presto si passa perché valga la pena dirlo. Meno di un giorno è rumore di pianificazione. */
export const GIORNI_PRIMA_CHE_CONTANO = 1;
/**
 * L'autista ci passerebbe **giorni prima** che la merce sia pronta?
 *
 * `null` quando non c'è niente da dire: ci passa dopo, ci passa lo stesso giorno, o il quando
 * del passaggio non si sa (un viaggio senza data non promette un giorno a nessuno).
 *
 * Si contano **giorni di calendario**, non ore: a chi pianifica «ci passa il 13 e la merce è
 * pronta il 15» dice qualcosa; «ci passa 46 ore prima» no. E un passaggio la mattina per una
 * merce pronta il pomeriggio **dello stesso giorno** non è un errore di pianificazione — è una
 * cosa che si aggiusta sul posto, e un avviso lì sarebbe quello che si impara a ignorare.
 */
export function ciPassaPrima(passaggio, pronto) {
    if (!(passaggio instanceof Date) || Number.isNaN(passaggio.getTime()))
        return null;
    // Giorni di calendario a Roma, non sull'orologio del PC che fa il conto.
    const giorni = calendarDaysBetween(passaggio, pronto.istante);
    if (giorni < GIORNI_PRIMA_CHE_CONTANO)
        return null;
    return { giorni, suUnaPresunzione: pronto.presunto };
}
/*
 * ## Le tre fasce, per chi assegna i viaggi
 *
 * Cosimo, 2026-10-27: *«Nella schermata di assegnazione dei viaggi agli autisti non si vede data
 * e ora di approntamento merce dei singoli viaggi. VALUTA ANCHE SE FARGLI UN SEGNO PER INDICARGLI
 * IL GIÀ PRONTO O IL PRONTO IN GIORNATA PIUTTOSTO CHE IL PRONTO NEI PROSSIMI GIORNI»*.
 *
 * - `pronta`: dichiarata, e l'istante e' gia' passato — si puo' caricare adesso;
 * - `oggi`: dichiarata per piu' tardi oggi, **oppure non dichiarata** (la regola del 12/10: si
 *   assume oggi). La presunzione resta visibile (`presunto`), ma la fascia e' «in giornata» e non
 *   «gia' pronta»: nessuno ha detto che lo e';
 * - `prossimi`: dichiarata per un giorno che non e' oggi, nel futuro.
 *
 * Giorni di **calendario** a Roma, come `ciPassaPrima`: chi pianifica pensa «oggi» e
 * «giovedi'», non «fra 31 ore».
 */
export const FASCE_PRONTO = ['pronta', 'oggi', 'prossimi'];
export function fasciaPronto(pronto, adesso) {
    if (pronto.presunto)
        return 'oggi';
    if (pronto.istante.getTime() <= adesso.getTime())
        return 'pronta';
    // La mezzanotte di domani a Roma: `setHours(24)` la prendeva sull'orologio del PC.
    const fine = giornoDaChiave(chiavePiuGiorni(dayKey(adesso), 1));
    return pronto.istante.getTime() < fine.getTime() ? 'oggi' : 'prossimi';
}
/**
 * Il riassunto per un viaggio, dai pronto-merce dichiarati (o `null`) dei suoi ritiri **ancora da
 * caricare**. `null` se non ce n'e' nessuno: un viaggio di sole consegne, o con tutto gia' a bordo,
 * non ha un pronto-merce da dire.
 */
export function prontoDelViaggio(dichiarati, adesso) {
    if (dichiarati.length === 0)
        return null;
    const ordine = { pronta: 0, oggi: 1, prossimi: 2 };
    let ultimo = null;
    let fascia = 'pronta';
    let pronti = 0;
    let presunti = 0;
    for (const d of dichiarati) {
        const p = prontoMerce(d, adesso);
        const f = fasciaPronto(p, adesso);
        if (f === 'pronta')
            pronti += 1;
        if (p.presunto)
            presunti += 1;
        /*
          L'ultimo e' quello della fascia piu' lontana, e dentro la stessa fascia quello con l'istante
          piu' tardo. Un presunto (inizio di oggi) non scavalca un «oggi alle 15» dichiarato: l'ora
          vera dice di piu' di quella supposta.
        */
        if (ultimo === null ||
            ordine[f] > ordine[fascia] ||
            (ordine[f] === ordine[fascia] && p.istante.getTime() > ultimo.istante.getTime())) {
            ultimo = p;
            fascia = f;
        }
    }
    return { fascia, ultimo: ultimo, ritiri: dichiarati.length, pronti, presunti };
}
//# sourceMappingURL=pronto-merce.js.map